<template>
  <AdminLayout>
    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <div class="animate-spin rounded-full h-32 w-32 border-b-2 border-mifo-accent"></div>
    </div>

    <!-- Course content -->
    <div v-else-if="course" class="space-y-6">
      <!-- Breadcrumb -->
      <div class="mb-6">
        <nav class="flex" aria-label="Breadcrumb">
          <ol class="flex items-center space-x-2">
            <li>
      <router-link 
        to="/herzbeben/courses" 
                class="text-sm text-gray-500 hover:text-mifo-accent transition-colors"
              >
                ← Zurück zu den Kursen
              </router-link>
            </li>
          </ol>
        </nav>
      </div>
      
      <!-- Header -->
      <div class="flex justify-between items-start">
        <div>
          <h1 class="text-2xl font-heading font-bold text-mifo-primary">
            {{ course.title }}
          </h1>
          <p class="mt-1 text-sm text-gray-500 flex items-center">
            <span>ID: {{ course.id }} • {{ course.city }} • </span>
            <router-link 
              :to="`/checkout/${course.id}`" target="_blank"
              class="ml-1 inline-flex items-center text-mifo-primary hover:text-mifo-accent transition-colors"
            >
              <ShoppingCart class="h-4 w-4 mr-1" />
              Zur Buchung
            </router-link>
          </p>
        </div>
        <div class="flex space-x-3">
          <button
            @click="editCourse"
            class="btn-outline"
          >
            <Pencil class="h-4 w-4 mr-2 inline-block align-middle" />
            Bearbeiten
          </button>
          <button
            @click="showManualBookingModal = true"
            class="btn-primary"
          >
            <UserPlus class="h-4 w-4 mr-2 inline-block align-middle" />
            Manuelle Buchung
          </button>
          <button
            @click="toggleCourseStatus"
            :class="[
              course.status === 'active' ? 'btn-secondary' : 'btn-primary'
            ]"
          >
            {{ course.status === 'active' ? 'Deaktivieren' : 'Aktivieren' }}
          </button>
        </div>
      </div>

      <!-- Course Info -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="card">
          <h3 class="text-lg font-heading font-semibold text-mifo-primary mb-4">
            Kursdetails
          </h3>
          <div class="space-y-3">
            <div>
              <span class="text-sm font-medium text-gray-500">Startdatum:</span>
              <p class="text-sm text-gray-900">{{ formatDate(course.startDate) }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Enddatum:</span>
              <p class="text-sm text-gray-900">{{ formatDate(course.endDate) }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Stadt:</span>
              <p class="text-sm text-gray-900">{{ course.city }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Status:</span>
              <span
                class="inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2"
                :class="[
                  course.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]"
              >
                {{ course.status === 'active' ? 'Aktiv' : 'Inaktiv' }}
              </span>
            </div>
          </div>
        </div>

        <div class="card">
          <h3 class="text-lg font-heading font-semibold text-mifo-primary mb-4">
            Verfügbarkeit
          </h3>
          <div class="space-y-3">
            <div>
              <span class="text-sm font-medium text-gray-500">Gesamtplätze:</span>
              <p class="text-2xl font-heading font-bold text-mifo-primary">{{ course.totalSpots }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Verkaufte Plätze:</span>
              <p class="text-2xl font-heading font-bold text-mifo-accent">{{ course.soldSpots }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Verfügbare Plätze:</span>
              <p class="text-2xl font-heading font-bold text-mifo-highlight">{{ course.totalSpots - course.soldSpots }}</p>
            </div>
            <div class="mt-4">
              <div class="flex justify-between text-sm text-gray-600 mb-1">
                <span>Auslastung</span>
                <span>{{ Math.round((course.soldSpots / course.totalSpots) * 100) }}%</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-mifo-accent h-2 rounded-full transition-all duration-300"
                  :style="{ width: `${(course.soldSpots / course.totalSpots) * 100}%` }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div class="card">
          <h3 class="text-lg font-heading font-semibold text-mifo-primary mb-4">
            Preise
          </h3>
          <div class="space-y-3">
            <div>
              <span class="text-sm font-medium text-gray-500">Standard:</span>
              <p class="text-lg font-semibold text-gray-900">{{ formatCurrency(course.prices?.standard || 0) }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Assistenzarzt:</span>
              <p class="text-lg font-semibold text-gray-900">{{ formatCurrency(course.prices?.assistenzarzt || 0) }}</p>
            </div>

          </div>
        </div>
      </div>

      <!-- Customer Orders -->
      <OrdersTable
        :title="'Bestellungen'"
        :orders="filteredOrders"
        :show-price-category="true"
        @row-click="viewOrderDetails"
      />

      <!-- Waiting List -->
      <div class="card">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-heading font-semibold text-mifo-primary">
            Warteliste ({{ waitingList.length }})
          </h2>
          <div class="flex space-x-2">
            <input
              v-model="waitingListSearchTerm"
              type="text"
              placeholder="E-Mail suchen..."
              class="input-field w-64"
            />
          </div>
        </div>

        <div v-if="waitingList.length === 0" class="text-center py-8">
          <p class="text-gray-500">Keine Einträge auf der Warteliste</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  E-Mail
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hinzugefügt am
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr 
                v-for="entry in filteredWaitingList" 
                :key="entry.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-mifo-primary">
                    {{ entry.email }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDate(entry.createdAt) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                    :class="[
                      entry.status === 'waiting' ? 'bg-yellow-100 text-yellow-800' :
                      entry.status === 'notified' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    ]"
                  >
                    {{ 
                      entry.status === 'waiting' ? 'Wartend' :
                      entry.status === 'notified' ? 'Benachrichtigt' :
                      'Registriert'
                    }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <button
                    @click="removeFromWaitingList(entry)"
                    class="btn-outline text-sm px-3 py-1"
                  >
                    Entfernen
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Error state -->
    <div v-else class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <h2 class="text-2xl font-heading font-bold text-gray-900 mb-4">Kurs nicht gefunden</h2>
        <p class="text-gray-600 mb-6">Der angeforderte Kurs konnte nicht geladen werden.</p>
        <router-link 
          to="/herzbeben/courses" 
          class="btn-primary"
        >
          ← Zurück zu den Kursen
        </router-link>
      </div>
    </div>

    <!-- Manual Booking Modal -->
    <div v-if="showManualBookingModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-xl font-heading font-semibold text-mifo-primary">
            Manuelle Buchung erstellen
          </h3>
          <button @click="showManualBookingModal = false" class="text-gray-400 hover:text-gray-600">
            <X class="h-6 w-6" />
          </button>
        </div>

        <form @submit.prevent="createManualBooking" class="space-y-6">
          <!-- Course and Price Selection -->
          <div class="card">
            <h4 class="text-lg font-heading font-semibold text-mifo-primary mb-4">Kurs & Preiskategorie</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Kurs</label>
                <input 
                  :value="course?.title" 
                  disabled 
                  class="input-field bg-gray-100"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Preiskategorie *</label>
                <select v-model="manualBookingForm.courseType" class="input-field" required>
                  <option value="">Bitte wählen</option>
                  <option value="standard">Standard ({{ formatCurrency(course?.prices?.standard || 0) }})</option>
                  <option value="assistenzarzt">Assistenzarzt ({{ formatCurrency(course?.prices?.assistenzarzt || 0) }})</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Customer Information -->
          <div class="card">
            <h4 class="text-lg font-heading font-semibold text-mifo-primary mb-4">Kundendaten</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Anrede *</label>
                <select v-model="manualBookingForm.customer.anrede" class="input-field" required>
                  <option value="">Bitte wählen</option>
                  <option value="herr">Herr</option>
                  <option value="frau">Frau</option>
                  <option value="keine">Keine Angabe</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                <input v-model="manualBookingForm.customer.titel" class="input-field" placeholder="z.B. Dr." />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Vorname *</label>
                <input v-model="manualBookingForm.customer.firstName" class="input-field" required />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Nachname *</label>
                <input v-model="manualBookingForm.customer.lastName" class="input-field" required />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">E-Mail *</label>
                <input v-model="manualBookingForm.customer.email" type="email" class="input-field" required />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                <input v-model="manualBookingForm.customer.phone" class="input-field" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Fachrichtung *</label>
                <select v-model="manualBookingForm.customer.fachrichtung" class="input-field" required>
                  <option value="">Bitte wählen</option>
                  <option value="allgemeinmedizin">Allgemeinmedizin (Hausarzt)</option>
                  <option value="anaesthesiologie">Anästhesiologie</option>
                  <option value="anatomie">Anatomie</option>
                  <option value="arbeitsmedizin">Arbeitsmedizin (Betriebsmedizin)</option>
                  <option value="chirurgie">Chirurgie</option>
                  <option value="herzchirurgie">Herzchirurgie</option>
                  <option value="innere_medizin">Innere Medizin</option>
                  <option value="innere_medizin_angiologie">Innere Medizin und Angiologie</option>
                  <option value="innere_medizin_endokrinologie">Innere Medizin und Endokrinologie und Diabetologie</option>
                  <option value="innere_medizin_gastroenterologie">Innere Medizin und Gastroenterologie</option>
                  <option value="innere_medizin_haematologie">Innere Medizin und Hämatologie und Onkologie</option>
                  <option value="innere_medizin_kardiologie">Innere Medizin und Kardiologie</option>
                  <option value="innere_medizin_nephrologie">Innere Medizin und Nephrologie</option>
                  <option value="innere_medizin_pneumologie">Innere Medizin und Pneumologie</option>
                  <option value="innere_medizin_rheumatologie">Innere Medizin und Rheumatologie</option>
                  <option value="kinder_jugendmedizin">Kinder- und Jugendmedizin</option>
                  <option value="mikrobiologie">Mikrobiologie, Virologie und Infektionsepidemiologie</option>
                  <option value="neurochirurgie">Neurochirurgie</option>
                  <option value="neurologie">Neurologie</option>
                  <option value="nuklearmedizin">Nuklearmedizin</option>
                  <option value="oeffentliches_gesundheitswesen">Öffentliches Gesundheitswesen</option>
                  <option value="pathologie">Pathologie</option>
                  <option value="pharmakologie">Pharmakologie</option>
                  <option value="physikalische_rehabilitative_medizin">Physikalische und Rehabilitative Medizin</option>
                  <option value="physiologie">Physiologie</option>
                  <option value="radiologie">Radiologie</option>
                  <option value="rechtsmedizin">Rechtsmedizin</option>
                  <option value="sonstige">Sonstige</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Schaller *</label>
                <select v-model="manualBookingForm.customer.schaller" class="input-field" required>
                  <option value="">Bitte wählen</option>
                  <option value="rechtsschaller">Rechtsschaller (Schallkopf in der rechten Hand)</option>
                  <option value="linksschaller">Linksschaller (Schallkopf in der linken Hand)</option>
                  <option value="noch_nicht_festgelegt">Noch nicht festgelegt</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Address Information -->
          <div class="card">
            <h4 class="text-lg font-heading font-semibold text-mifo-primary mb-4">Adresse</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Straße *</label>
                <input v-model="manualBookingForm.customer.street" class="input-field" required />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Hausnummer *</label>
                <input v-model="manualBookingForm.customer.houseNumber" class="input-field" required />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">PLZ *</label>
                <input v-model="manualBookingForm.customer.postalCode" class="input-field" required />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Ort *</label>
                <input v-model="manualBookingForm.customer.city" class="input-field" required />
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Land *</label>
                <select v-model="manualBookingForm.customer.country" class="input-field" required>
                  <option value="">Bitte wählen</option>
                  <option value="DE">Deutschland</option>
                  <option value="AT">Österreich</option>
                  <option value="CH">Schweiz</option>
                  <option value="US">Vereinigte Staaten</option>
                  <option value="GB">Großbritannien</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Billing Address -->
          <div class="card">
            <h4 class="text-lg font-heading font-semibold text-mifo-primary mb-4">Rechnungsadresse</h4>
            <div class="mb-4">
              <label class="flex items-center">
                <input 
                  v-model="manualBookingForm.differentBilling" 
                  type="checkbox" 
                  class="h-4 w-4 text-mifo-accent focus:ring-mifo-accent border-gray-300 rounded" 
                />
                <span class="ml-2 text-sm text-gray-700">Abweichende Rechnungsadresse</span>
              </label>
            </div>
            
            <div v-if="manualBookingForm.differentBilling" class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Institution</label>
                <input v-model="manualBookingForm.billing.institution" class="input-field" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Abteilung</label>
                <input v-model="manualBookingForm.billing.department" class="input-field" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Anrede</label>
                <select v-model="manualBookingForm.billing.anrede" class="input-field">
                  <option value="">Bitte wählen</option>
                  <option value="herr">Herr</option>
                  <option value="frau">Frau</option>
                  <option value="keine">Keine Angabe</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                <input v-model="manualBookingForm.billing.titel" class="input-field" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Vorname</label>
                <input v-model="manualBookingForm.billing.firstName" class="input-field" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Nachname</label>
                <input v-model="manualBookingForm.billing.lastName" class="input-field" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Straße</label>
                <input v-model="manualBookingForm.billing.street" class="input-field" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Hausnummer</label>
                <input v-model="manualBookingForm.billing.houseNumber" class="input-field" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">PLZ</label>
                <input v-model="manualBookingForm.billing.postalCode" class="input-field" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Ort</label>
                <input v-model="manualBookingForm.billing.city" class="input-field" />
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Land</label>
                <select v-model="manualBookingForm.billing.country" class="input-field">
                  <option value="">Bitte wählen</option>
                  <option value="DE">Deutschland</option>
                  <option value="AT">Österreich</option>
                  <option value="CH">Schweiz</option>
                  <option value="US">Vereinigte Staaten</option>
                  <option value="GB">Großbritannien</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Summary -->
          <div class="card">
            <h4 class="text-lg font-heading font-semibold text-mifo-primary mb-4">Zusammenfassung</h4>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600">Kurs:</span>
                <span class="font-medium">{{ course?.title }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Preiskategorie:</span>
                <span class="font-medium">{{ getCourseTypeName(manualBookingForm.courseType) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Betrag:</span>
                <span class="font-bold text-mifo-primary">{{ formatCurrency(getSelectedPrice()) }}</span>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end space-x-3">
            <button 
              type="button" 
              @click="showManualBookingModal = false" 
              class="btn-outline"
            >
              Abbrechen
            </button>
            <button 
              type="submit" 
              :disabled="!isManualBookingFormValid || creatingManualBooking"
              class="btn-primary"
            >
              <span v-if="creatingManualBooking" class="animate-spin">⏳</span>
              <span v-else>Buchung erstellen</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AdminLayout from '@/components/Layout/AdminLayout.vue'
import OrdersTable from '@/components/Admin/OrdersTable.vue'
import { Pencil, ShoppingCart, UserPlus, X } from 'lucide-vue-next'
import { formatCurrency, formatDate } from 'shared/utils'
import api from '@/utils/axios'

export default {
  name: 'CourseDetail',
  components: {
    AdminLayout,
    Pencil,
    ShoppingCart,
    UserPlus,
    X,
    OrdersTable
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const course = ref(null)
    const orders = ref([])
    const waitingList = ref([])
    const loading = ref(true)
    const searchTerm = ref('')
    const statusFilter = ref('')
    const waitingListSearchTerm = ref('')
    const showManualBookingModal = ref(false)
    const creatingManualBooking = ref(false)

    // Manual booking form data
    const manualBookingForm = reactive({
      courseType: '',
      customer: {
        anrede: '',
        titel: '',
        firstName: '',
        lastName: '',
        fachrichtung: '',
        schaller: '',
        street: '',
        houseNumber: '',
        postalCode: '',
        city: '',
        country: '',
        email: '',
        phone: ''
      },
      billing: {
        institution: '',
        department: '',
        anrede: '',
        titel: '',
        firstName: '',
        lastName: '',
        street: '',
        houseNumber: '',
        postalCode: '',
        city: '',
        country: ''
      },
      differentBilling: false
    })

    const filteredOrders = computed(() => {
      let filtered = [...orders.value].filter(order => order.customer) // Only show orders with customer data
      
      if (searchTerm.value) {
        const term = searchTerm.value.toLowerCase()
        filtered = filtered.filter(order => 
          order.customer?.firstName?.toLowerCase().includes(term) ||
          order.customer?.lastName?.toLowerCase().includes(term) ||
          order.customer?.email?.toLowerCase().includes(term)
        )
      }
      
      if (statusFilter.value) {
        filtered = filtered.filter(order => order.status === statusFilter.value)
      }
      
      return filtered
    })

    const filteredWaitingList = computed(() => {
      let filtered = [...waitingList.value]
      
      if (waitingListSearchTerm.value) {
        const term = waitingListSearchTerm.value.toLowerCase()
        filtered = filtered.filter(entry => 
          entry.email.toLowerCase().includes(term)
        )
      }
      
      return filtered
    })

    const fetchCourseData = async () => {
      loading.value = true
      try {
        const [courseResponse, ordersResponse, waitingListResponse] = await Promise.all([
        api.get(`/courses/admin/${route.params.id}`),
          api.get(`/courses/${route.params.id}/bookings`),
          api.get(`/waiting-list/course/${route.params.id}`)
        ])
        
        course.value = courseResponse.data
        orders.value = ordersResponse.data
        waitingList.value = waitingListResponse.data.waitingList || []
      } catch (error) {
        console.error('Error fetching course data:', error)
        // Show error state instead of mock data
        course.value = null
        orders.value = []
        waitingList.value = []
      } finally {
        loading.value = false
      }
    }

    const editCourse = () => {}

    const toggleCourseStatus = async () => {
      try {
        const newStatus = course.value.status === 'active' ? 'inactive' : 'active'
        await api.patch(`/courses/${course.value.id}`, { status: newStatus })
        course.value.status = newStatus
      } catch (error) {
        console.error('Error updating course status:', error)
      }
    }

    const getCourseTypeName = (type) => {
      const types = {
        'standard': 'Standard',
        'assistenzarzt': 'Assistenzarzt'
      }
      return types[type] || type
    }

    const viewOrderDetails = (order) => {
      // Navigate to course-scoped order detail page
      router.push(`/herzbeben/courses/${course.value.id}/order/${order.id}`)
    }

    const refundOrder = async (order) => {
      if (confirm(`Möchten Sie die Bestellung von ${order.customer.firstName} ${order.customer.lastName} wirklich rückerstatten?`)) {
        try {
          await api.post(`/orders/${order.id}/refund`)
          // Refresh orders
          await fetchCourseData()
        } catch (error) {
          console.error('Error refunding order:', error)
        }
      }
    }

    const removeFromWaitingList = async (entry) => {
      if (confirm(`Möchten Sie ${entry.email} von der Warteliste entfernen?`)) {
        try {
          await api.delete(`/waiting-list/${entry.id}`)
          // Refresh waiting list
          await fetchCourseData()
        } catch (error) {
          console.error('Error removing from waiting list:', error)
        }
      }
    }

    // Manual booking computed properties and methods
    const isManualBookingFormValid = computed(() => {
      return manualBookingForm.courseType &&
             manualBookingForm.customer.anrede &&
             manualBookingForm.customer.firstName &&
             manualBookingForm.customer.lastName &&
             manualBookingForm.customer.fachrichtung &&
             manualBookingForm.customer.schaller &&
             manualBookingForm.customer.street &&
             manualBookingForm.customer.houseNumber &&
             manualBookingForm.customer.postalCode &&
             manualBookingForm.customer.city &&
             manualBookingForm.customer.country &&
             manualBookingForm.customer.email
    })

    const getSelectedPrice = () => {
      if (!course.value || !manualBookingForm.courseType) return 0
      
      if (manualBookingForm.courseType === 'standard') {
        return course.value.prices?.standard || 0
      } else if (manualBookingForm.courseType === 'assistenzarzt') {
        return course.value.prices?.assistenzarzt || 0
      }
      return 0
    }

    const createManualBooking = async () => {
      if (!isManualBookingFormValid.value) return
      
      creatingManualBooking.value = true
      try {
        const payload = {
          type: 'course',
          courseId: course.value.id,
          courseType: manualBookingForm.courseType,
          customer: manualBookingForm.customer,
          billing: manualBookingForm.differentBilling ? manualBookingForm.billing : null,
          totalAmount: getSelectedPrice(),
          paymentMethod: 'banktransfer', // Manual bookings use bank transfer
          manualBooking: true // Flag to indicate this is a manual booking
        }

        const response = await api.post('/orders', payload)
        
        // Reset form
        Object.assign(manualBookingForm, {
          courseType: '',
          customer: {
            anrede: '',
            titel: '',
            firstName: '',
            lastName: '',
            fachrichtung: '',
            schaller: '',
            street: '',
            houseNumber: '',
            postalCode: '',
            city: '',
            country: '',
            email: '',
            phone: ''
          },
          billing: {
            institution: '',
            department: '',
            anrede: '',
            titel: '',
            firstName: '',
            lastName: '',
            street: '',
            houseNumber: '',
            postalCode: '',
            city: '',
            country: ''
          },
          differentBilling: false
        })

        showManualBookingModal.value = false
        
        // Refresh course data to show new booking
        await fetchCourseData()
        
        // Show success message
        alert('Manuelle Buchung erfolgreich erstellt!')
        
        // Navigate to the new order if created
        if (response.data?.orders?.[0]?.id) {
          router.push(`/herzbeben/courses/${course.value.id}/order/${response.data.orders[0].id}`)
        }
      } catch (error) {
        console.error('Error creating manual booking:', error)
        alert('Fehler beim Erstellen der manuellen Buchung: ' + (error.response?.data?.error || error.message))
      } finally {
        creatingManualBooking.value = false
      }
    }

    onMounted(() => {
      fetchCourseData()
    })

    return {
      course,
      orders,
      waitingList,
      filteredOrders,
      filteredWaitingList,
      searchTerm,
      statusFilter,
      waitingListSearchTerm,
      loading,
      formatCurrency,
      formatDate,
      editCourse,
      toggleCourseStatus,
      getCourseTypeName,
      viewOrderDetails,
      refundOrder,
      removeFromWaitingList,
      showManualBookingModal,
      manualBookingForm,
      creatingManualBooking,
      isManualBookingFormValid,
      getSelectedPrice,
      createManualBooking
    }
  }
}
</script> 